import React, {useEffect, useState} from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    TableCell,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TextField,
    Stack, Button
} from '@mui/material';
import {useAppSelector, useAppDispatch} from '../../app/hooks';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import {getPlayers, IPlayer, setItemsPerPage, setPageNumber} from './playersSlice';
import {DateTime} from "luxon";
import {
    editButton,
    editIcon,
    searchField,
    searchInner,
    tableCell,
    tableContainer,
    tableHeaderCell
} from "./playersStyles";
import TablesPagination from "../../components/TablesPagination/TablesPagination";
import {useSortingAndSearch} from "./hooks/useSortingAndSearch";
import PlayerForm from "./components/PlayerForm";
import {createGetUsersQuery} from "../../helpers/createGetUsersQuery";


interface ITableHeaders {
    id: keyof IPlayer;
    label: string
}

const tableHeaders: ITableHeaders[] = [
    {id: 'name', label: 'Name'},
    {id: 'surname', label: 'Surname'},
    {id: 'nickname', label: 'Nickname'},
    {id: 'displayname', label: 'Display Name'},
    {id: 'dateOfBirth', label: 'Date Of Birth'},
    {id: 'socialMedia', label: 'Social Media'},
    {id: 'contact', label: 'Contact'}
];

const PlayersPage: React.FC = () => {
    const [isOpenForm, setIsOpenForm] = useState<boolean>(false);
    const [editingPlayer, setEditingPlayer] = useState<IPlayer | null>(null);
    const {sorting, searchValue, setSearchValue, handleSorting} = useSortingAndSearch();
    const dispatch = useAppDispatch();
    const {players, totalCount, pageNumber, itemsPerPage} = useAppSelector((state) => state.players);


    useEffect(() => {
        const queryParams = createGetUsersQuery(
            pageNumber,
            itemsPerPage,
            sorting,
            searchValue
        )
        dispatch(getPlayers(queryParams));
    }, []);


    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        dispatch(setPageNumber(newPage))

        const queryParams = createGetUsersQuery(
            newPage + 1,
            itemsPerPage,
            sorting,
            searchValue
        )
        dispatch(getPlayers(queryParams));
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setPageNumber(0));
        dispatch(setItemsPerPage(+event.target.value))

        const queryParams = createGetUsersQuery(
            1,
            +event.target.value,
            sorting,
            searchValue
        )
        dispatch(getPlayers(queryParams));
    }

    const handleEditPlayer = (currentPlayer: IPlayer) => {
        setEditingPlayer(currentPlayer);
        setIsOpenForm(true);
    }

    const onCloseForm = () => {
        setEditingPlayer(null);
        setIsOpenForm(false);
    }

    return (
        <Container sx={{mt: 4}}>
            {isOpenForm &&
                <PlayerForm
                    editingPlayer={editingPlayer}
                    onCloseForm={onCloseForm}
                />
            }

            <Typography variant="h4" gutterBottom>
                Players
            </Typography>
            <Box sx={{mt: 4}}>
                <Box sx={searchInner}>
                    <Stack spacing={2} sx={{width: 300}}>
                        <TextField
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            label="Search"
                            sx={searchField}
                        />
                    </Stack>
                    <Button onClick={()=>setIsOpenForm(true)}>
                        Create
                    </Button>
                </Box>
                <Paper sx={{width: '100%', overflow: 'hidden'}}>
                    <TableContainer sx={tableContainer}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {tableHeaders.map(header => (
                                        <TableCell
                                            sx={(theme) => ({
                                                backgroundColor: theme.palette.mode === 'dark' ? "#3e3c3c" : "#f0f0f0",
                                                ...tableHeaderCell,
                                                '&:hover': {
                                                    backgroundColor:
                                                        theme.palette.mode === 'dark' ? '#90caf9' : '#9cbed9',
                                                },
                                            })}
                                            key={header.id}
                                            onClick={() => handleSorting(header.id)}
                                        >
                                            {header.label}
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        sx={(theme) => ({
                                            backgroundColor: theme.palette.mode === 'dark' ? "#3e3c3c" : "#f0f0f0",
                                            fontWeight: "600",
                                            cursor: "default"
                                        })}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {players.map(player => {
                                    return (
                                        <TableRow key={player.id}>
                                            <TableCell sx={{...tableCell}}>{player.name}</TableCell>
                                            <TableCell sx={{...tableCell}}>{player.surname}</TableCell>
                                            <TableCell sx={{...tableCell}}>{player.nickname}</TableCell>
                                            <TableCell sx={{...tableCell}}>{player.displayname}</TableCell>
                                            <TableCell
                                                sx={{...tableCell}}>{DateTime.fromISO(player.dateOfBirth).toFormat("dd/MM/yyyy")}</TableCell>
                                            <TableCell sx={{...tableCell}}>
                                                {
                                                    player.socialMedia &&
                                                    <Box>
                                                        {player.socialMedia.facebook &&
                                                            <p>facebook: {player.socialMedia.facebook}</p>
                                                        }
                                                        {player.socialMedia.linkedIn &&
                                                            <p>linkedIn: {player.socialMedia.linkedIn}</p>
                                                        }
                                                        {player.socialMedia.comments &&
                                                            <p>comments: {player.socialMedia.comments}</p>
                                                        }
                                                    </Box>
                                                }
                                            </TableCell>
                                            <TableCell sx={{...tableCell}}>
                                                {
                                                    player.contact &&
                                                    <Box>
                                                        <p>
                                                            {player.contact.country}
                                                            {player.contact.hometown && `,${player.contact.hometown}`}
                                                        </p>
                                                        <p>{player.contact.telephon}</p>
                                                        <p>{player.contact.email}</p>
                                                    </Box>
                                                }
                                            </TableCell>

                                            <TableCell sx={{...tableCell, textAlign: "center"}}
                                            >
                                                <Button
                                                    sx={editButton}
                                                    onClick={() => handleEditPlayer(player)}
                                                >
                                                    <BorderColorIcon sx={editIcon}/>
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablesPagination
                        totalCount={totalCount}
                        itemsPerPage={itemsPerPage}
                        pageNumber={pageNumber}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
        </Container>
    );
};

export default PlayersPage;
